import {
  FilterBarContainer,
  FilterBarLeftContent,
  FilterBarRightContent,
  FiltersContainerLeftContainer,
  FiltersContainerRightContainer,
} from "./FilterBar.styled";
import {
  Select,
  Button,
  TextField,
  SwitchMode,
  TabSwitcher,
} from "@ann/web-components";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTriggers } from "../../context/TriggersContext";

const FilterBar = ({ mode, setMode }) => {
  const { filters, updateFilters } = useTriggers();
  const [searchTermType, setSearchTermType] = useState("");
  const [searchTermLastUpdate, setSearchTermLastUpdate] = useState("");
  const [searchTermState, setSearchTermState] = useState("");
  const [searchTermOwner, setSearchTermOwner] = useState("");
  const [searchValue, setSearchValue] = useState(filters?.search || "");
  const [filtersKey, setFiltersKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const searchTimeoutRef = useRef(null);
  
 
  const getInitialTab = () => {
    if (location.pathname.includes('/templates')) {
      return "templates";
    }
    return "created";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  
  useEffect(() => {
    const newTab = getInitialTab();
    setSelectedTab(newTab);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    if (tabId === "created") {
      navigate(`/v2/workflows`);
   } else if (tabId === "templates") {
      navigate(`/v2/workflows/templates`);
    } else if (tabId === "logs") {
      navigate(`/v2/workflows/logs`);
    }
  };

  // Sync local state with context when it changes
  useEffect(() => {
    setSearchValue(filters?.search || "");
  }, [filters?.search]);

  const typeOptions = [
    { label: "Events", value: "events" },
    { label: "Customer", value: "customer" },
    { label: "Support", value: "support" },
    { label: "Finance", value: "finance" },
  ];

  const stateOptions = [
    { label: "Not set up yet", value: "not_set_up_yet" },
    { label: "Active", value: "active" },
    { label: "Paused", value: "paused" },
    { label: "Scheduled", value: "scheduled" },
    { label: "Auto deactivated", value: "auto_deactivated" },
    { label: "Disabled", value: "disabled" },
    { label: "Archived", value: "archived" },
    { label: "Disabled (System)", value: "disabled_system" },
  ];

  const lastUpdateOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This year", value: "this_year" },
  ];

  const ownerOptions = [
    { label: "John Doe", value: "john_doe" },
    { label: "Jane Smith", value: "jane_smith" },
    { label: "Mike Johnson", value: "mike_johnson" },
    { label: "Sarah Wilson", value: "sarah_wilson" },
    { label: "Alex Brown", value: "alex_brown" },
    { label: "Lisa Davis", value: "lisa_davis" },
    { label: "David Chen", value: "david_chen" },
    { label: "Emma Wilson", value: "emma_wilson" },
    { label: "Tom Anderson", value: "tom_anderson" },
    { label: "Rachel Green", value: "rachel_green" },
    { label: "Chris Lee", value: "chris_lee" },
    { label: "Maria Garcia", value: "maria_garcia" },
    { label: "Kevin Park", value: "kevin_park" },
    { label: "Sophie Turner", value: "sophie_turner" },
    { label: "James Bond", value: "james_bond" },
  ];

  const filteredType = useMemo(() => {
    if (!searchTermType.trim()) return typeOptions;
    const searchLower = searchTermType.toLowerCase();
    return typeOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermType]);

  const filteredLastUpdate = useMemo(() => {
    if (!searchTermLastUpdate.trim()) return lastUpdateOptions;
    const searchLower = searchTermLastUpdate.toLowerCase();
    return lastUpdateOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermLastUpdate]);

  const filteredState = useMemo(() => {
    if (!searchTermState.trim()) return stateOptions;
    const searchLower = searchTermState.toLowerCase();
    return stateOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermState]);

  const filteredOwner = useMemo(() => {
    if (!searchTermOwner.trim()) return ownerOptions;
    const searchLower = searchTermOwner.toLowerCase();
    return ownerOptions.filter(
      (owner) =>
        owner.label.toLowerCase().includes(searchLower) ||
        owner.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermOwner]);

  const handleMultipleSelectionType = (items) => {
    updateFilters({ type: items });
  };

  const handleMultipleSelectionLastUpdate = (items) => {
    updateFilters({ lastUpdate: items });
  };

  const handleMultipleSelectionState = (items) => {    
    updateFilters({ state: items });
  };

  const handleSearchOwner = (searchValue) => {    
    setSearchTermOwner(searchValue);
  };

  const handleMultipleSelectionOwner = (items) => {    
    updateFilters({ owner: items.map(item => item.value) });
  };

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms debounce
    searchTimeoutRef.current = setTimeout(() => {
      updateFilters({ search: value });
    }, 500);
  }, [updateFilters]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleClearAllFilters = () => {
    setSearchValue("");
    setSearchTermType("");
    setSearchTermLastUpdate("");
    setSearchTermState("");
    setSearchTermOwner("");
    setFiltersKey((prev) => prev + 1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    updateFilters({
      search: '',
      type: [],
      lastUpdate: '',
      state: [],
      owner: []
    });
  };

  const emptyOrValue = (arr) => (arr.length > 0 ? arr : undefined);

  return (
    <FilterBarContainer>
      <FilterBarLeftContent>
        <FiltersContainerLeftContainer>
          <TabSwitcher
            key={selectedTab}
            defaultSelectedId={selectedTab}
            size="medium"
            onChange={handleTabChange}
          >
            <TabSwitcher.Button id="created" label="Created" />
            <TabSwitcher.Button id="templates" label="Templates" />
            <TabSwitcher.Button id="logs" label="Audit Log" />
          </TabSwitcher>
        </FiltersContainerLeftContainer>
        <FiltersContainerRightContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search workflows..."
            value={searchValue}
          />
          <Select
            key={`type-filter-${filtersKey}`}
            label="Type"  
            placeholder="Type"
            onChange={handleMultipleSelectionType}
            size="small"
            value={emptyOrValue(filters.type)}
            width="auto"
            minWidth="139px"
            disabled={true}
          >
            <Select.Menu>
              {filteredType.map((type) => (
                <Select.Item
                  key={type.value}
                  value={type.value}
                  label={type.label}
                >
                  {type.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`last-update-filter-${filtersKey}`}
            label="Last Update"
            placeholder="Last Update"
            onChange={handleMultipleSelectionLastUpdate}
            size="small"
            value={emptyOrValue(filters.lastUpdate)}
            width="auto"
            minWidth="139px"
            disabled={true}
          >
            <Select.Menu>
              {filteredLastUpdate.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`state-filter-${filtersKey}`}
            label="Status"
            placeholder="Status"
            onChange={handleMultipleSelectionState}
            size="small"
            value={emptyOrValue(filters.state)}
            width="auto"
            minWidth="139px"
            disabled={true}
          >
            <Select.Menu>
              {filteredState.map((state) => (
                <Select.Item
                  key={state.value}
                  value={state.value}
                  label={state.label}
                >
                  {state.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`owner-filter-${filtersKey}`}
            label="Owner"
            placeholder="Owner"
            onSelectionChange={handleMultipleSelectionOwner}
            size="small"
            value={emptyOrValue(filters.owner)}
            width="auto"
            minWidth="144px"
            disabled={true}
          >
            <Select.Menu multipleChoice={true}>
              <Select.Search
                placeholder="Search a name"
                onChange={handleSearchOwner}
                label=""
              />
              {filteredOwner.map((owner) => (
                <Select.Item
                  key={owner.value}
                  value={owner.value}
                  label={owner.label}
                >
                  {owner.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFilters}
          />
        </FiltersContainerRightContainer>
      </FilterBarLeftContent>
      {setMode && (
        <FilterBarRightContent>
          <SwitchMode mode={mode} onModeChange={setMode} />
        </FilterBarRightContent>
      )}
    </FilterBarContainer>
  );
};

export default FilterBar;
